import { api } from "./client";
import type {
  AdminLoginResponse,
  DashboardStats,
  Ingredient,
  PaginatedResponse,
  User,
  UserDetail,
  Venue,
  VenueDetail,
} from "@/types";

// Auth
export const login = (email: string, password: string) =>
  api.post<AdminLoginResponse>("/auth/login", { email, password }).then((r) => r.data);

export const forgotPassword = (email: string) =>
  api.post<{ success: boolean }>("/auth/forgot-password", { email }).then((r) => r.data);

export const resetPassword = (email: string, code: string, password: string) =>
  api.post<{ success: boolean }>("/auth/reset-password", { email, code, password }).then((r) => r.data);

// Stats
export const getStats = () =>
  api.get<DashboardStats>("/stats").then((r) => r.data);

// Users
export const getUsers = (params: { page?: number; limit?: number; search?: string }) =>
  api.get<PaginatedResponse<User>>("/users", { params }).then((r) => r.data);

export const getUser = (id: string) =>
  api.get<{ user: User; venues: Venue[] }>(`/users/${id}`).then((r) => ({ ...r.data.user, venues: r.data.venues }) as UserDetail);

export const deleteUser = (id: string) =>
  api.delete(`/users/${id}`).then((r) => r.data);

// Venues
export const getVenues = (params: { page?: number; limit?: number; search?: string; planType?: string }) =>
  api.get<PaginatedResponse<Venue>>("/venues", { params }).then((r) => r.data);

export const getVenue = (id: string) =>
  api.get<{ venue: VenueDetail }>(`/venues/${id}`).then((r) => r.data.venue);

export const updateVenue = (id: string, data: Partial<Venue>) =>
  api.patch<{ venue: Venue }>(`/venues/${id}`, data).then((r) => r.data.venue);

export const deleteVenue = (id: string) =>
  api.delete(`/venues/${id}`).then((r) => r.data);

// Ingredients
export const getIngredients = (params: { search?: string; category?: string }) =>
  api.get<{ ingredients: Ingredient[] }>("/ingredients", { params }).then((r) => r.data.ingredients);

export const createIngredient = (data: Omit<Ingredient, "_id">) =>
  api.post<{ ingredient: Ingredient }>("/ingredients", data).then((r) => r.data.ingredient);

export const updateIngredient = (id: string, data: Partial<Ingredient>) =>
  api.patch<{ ingredient: Ingredient }>(`/ingredients/${id}`, data).then((r) => r.data.ingredient);

export const deleteIngredient = (id: string) =>
  api.delete(`/ingredients/${id}`).then((r) => r.data);

export const bulkImportIngredients = (items: Record<string, unknown>[]) =>
  api.post<{ success: boolean; added: number; skipped: number; total: number }>("/ingredients/bulk", items).then((r) => r.data);

// App Settings
export const getAppSettings = () =>
  api.get<{ settings: Record<string, string> }>("/settings").then((r) => r.data.settings);

export const updateAppSettings = (settings: Record<string, string>) =>
  api.put("/settings", settings).then((r) => r.data);

// Plans
export const getPlans = () =>
  api.get<{ plans: Plan[] }>("/plans").then((r) => r.data.plans);

export const createPlan = (data: Partial<Plan>) =>
  api.post<{ plan: Plan }>("/plans", data).then((r) => r.data.plan);

export const updatePlan = (id: string, data: Partial<Plan>) =>
  api.patch<{ plan: Plan }>(`/plans/${id}`, data).then((r) => r.data.plan);

export const deletePlan = (id: string) =>
  api.delete(`/plans/${id}`).then((r) => r.data);

export interface Plan {
  _id: string;
  type: string;
  name: string;
  description?: string;
  priceMonthly: number;
  priceYearly: number;
  priceTryMonthly: number;
  priceTryYearly: number;
  aiAddonPriceUsd: number;
  aiAddonPriceTry: number;
  limits: {
    maxMenus: number;
    maxItems: number;
    maxCategories: number;
    maxImages: number;
    maxQrCodes: number;
    maxLanguages: number;
  };
  features: {
    analytics: boolean;
    analyticsDays: number;
    customTheme: boolean;
    customCss: boolean;
    removeBranding: boolean;
    customDomain: boolean;
    pdfExport: boolean;
    orderEnabled: boolean;
    aiTranslate: boolean;
    aiImageGeneration: boolean;
  };
  welcomeCredits: number;
  presets: string[];
  isActive: boolean;
  sortOrder: number;
}

// Backgrounds
export interface BackgroundImage {
  _id: string;
  name: string;
  url: string;
  category: string;
  paddingTop: number;
  paddingBottom: number;
  paddingX: number;
  paddingTopDesktop: number;
  paddingBottomDesktop: number;
  paddingXDesktop: number;
}

export const getBackgrounds = () =>
  api.get<{ backgrounds: BackgroundImage[] }>("/backgrounds").then((r) => r.data.backgrounds);

export const createBackground = (data: { name: string; url: string; category?: string }) =>
  api.post("/backgrounds", data).then((r) => r.data);

export const updateBackground = (id: string, data: Partial<BackgroundImage>) =>
  api.patch<{ background: BackgroundImage }>(`/backgrounds/${id}`, data).then((r) => r.data.background);

export const deleteBackground = (id: string) =>
  api.delete(`/backgrounds/${id}`).then((r) => r.data);

export const uploadAdminImage = (file: File) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post<{ url: string; originalSize: number; optimizedSize: number }>("/upload", formData).then((r) => r.data);
};

// Custom Domains
export interface CustomDomainVenue {
  _id: string;
  name: string;
  slug: string;
  customDomain: string;
  customDomainStatus: "pending" | "active" | "failed";
  userId: { _id: string; name: string; email: string };
}

export const getCustomDomains = () =>
  api.get<{ venues: CustomDomainVenue[] }>("/custom-domains").then((r) => r.data.venues);

export const updateDomainStatus = (id: string, status: "pending" | "active" | "failed") =>
  api.patch<{ venue: CustomDomainVenue }>(`/custom-domains/${id}/status`, { status }).then((r) => r.data.venue);

// Payments
export interface AdminPayment {
  _id: string;
  userId: { _id: string; name: string; email: string };
  venueId: { _id: string; name: string; slug: string; venueNumber?: number };
  planType: string;
  period: string;
  amount: number;
  currency: string;
  status: "pending" | "success" | "failed";
  iyzicoPaymentId?: string;
  createdAt: string;
}

export const getPayments = (params?: { userId?: string; venueId?: string; status?: string; limit?: number; offset?: number }) =>
  api.get<{ payments: AdminPayment[]; total: number }>("/payments", { params }).then((r) => r.data);

// Credit Packages
export interface CreditPackageType {
  _id: string;
  credits: number;
  priceTry: number;
  priceUsd: number;
  isActive: boolean;
  sortOrder: number;
}

export const getCreditPackages = () =>
  api.get<{ packages: CreditPackageType[] }>("/credit-packages").then((r) => r.data.packages);

export const createCreditPackage = (data: Partial<CreditPackageType>) =>
  api.post<{ package: CreditPackageType }>("/credit-packages", data).then((r) => r.data.package);

export const updateCreditPackage = (id: string, data: Partial<CreditPackageType>) =>
  api.patch<{ package: CreditPackageType }>(`/credit-packages/${id}`, data).then((r) => r.data.package);

export const deleteCreditPackage = (id: string) =>
  api.delete(`/credit-packages/${id}`).then((r) => r.data);

// Storage
export interface StorageFile {
  key: string;
  url: string;
  size: number;
  lastModified: string;
  folder: string;
}

export const getStorageFiles = (folder?: string) =>
  api.get<{ files: StorageFile[]; totalSize: number }>("/storage", { params: { folder } }).then((r) => r.data);

export const deleteStorageFile = (url: string) =>
  api.delete("/storage", { data: { url } }).then((r) => r.data);

// Email Test
export const sendTestEmail = (to: string) =>
  api.post<{ success: boolean; message: string }>("/email/test", { to }).then((r) => r.data);

// Event Logs
export const getEvents = (params: { type?: string; level?: string; venueId?: string; limit?: number; offset?: number }) =>
  api.get<{ events: EventLog[]; total: number }>("/events", { params }).then((r) => r.data);

export const getEventStats = () =>
  api.get<EventStats>("/events/stats").then((r) => r.data);

export interface EventLog {
  _id: string;
  type: string;
  level: "info" | "warn" | "error";
  message: string;
  venueId?: string;
  userId?: string;
  meta?: Record<string, unknown>;
  ip?: string;
  userAgent?: string;
  createdAt: string;
}

export interface EventStats {
  today: number;
  errorsToday: number;
  lastHour: number;
  byType: { type: string; count: number }[];
}

// Trash / Restore — soft-deleted entities awaiting the 30-day retention purge.
export type TrashModel = "Venue" | "LoyaltyRule" | "Review";

export interface TrashItem {
  _id: string;
  deletedAt: string;
  // Each model carries its own extra fields — display layer narrows what to show.
  [key: string]: unknown;
}

export const getTrash = (model: TrashModel, params?: { limit?: number; offset?: number }) =>
  api
    .get<{ model: TrashModel; items: TrashItem[]; total: number; limit: number; offset: number }>(
      "/trash",
      { params: { model, ...params } }
    )
    .then((r) => r.data);

export const restoreItem = (model: TrashModel, id: string) =>
  api.post<{ success: boolean; item: TrashItem }>("/restore", { model, id }).then((r) => r.data);

// Email templates — every known template carries a hardcoded default; the
// admin override lives in DB. `isCustomized` says whether the admin has
// edited that key. DELETE clears the override and falls back to default.
export interface EmailTemplateRow {
  key: string;
  description: string;
  variables: string[];
  subject: string;
  html: string;
  isCustomized: boolean;
  updatedAt?: string;
}

export interface EmailTemplateDetail extends EmailTemplateRow {
  defaults: { subject: string; html: string };
}

export const listEmailTemplates = () =>
  api.get<{ items: EmailTemplateRow[] }>("/email-templates").then((r) => r.data.items);

export const getEmailTemplate = (key: string) =>
  api.get<EmailTemplateDetail>(`/email-templates/${key}`).then((r) => r.data);

export const updateEmailTemplate = (key: string, data: { subject: string; html: string }) =>
  api.patch<{ item: EmailTemplateRow }>(`/email-templates/${key}`, data).then((r) => r.data.item);

export const resetEmailTemplate = (key: string) =>
  api.delete<{ success: boolean }>(`/email-templates/${key}`).then((r) => r.data);

export const testEmailTemplate = (key: string, to: string) =>
  api.post<{ success: boolean }>(`/email-templates/${key}/test`, { to }).then((r) => r.data);
